import { LINK_BIO_BUILT_IN_BUTTON_IDS, type LinkBioPreviewEditSection } from '@repo/shared/marketing';
import { listLinkBioErrorPaths } from './link-bio-editor-error-paths';
import type { LinkBioFormErrors } from './link-bio-editor-types';

/** The editor's three tabs (also the `?tab=` search param). */
export const LINK_BIO_EDITOR_TABS = ['profile', 'links', 'design'] as const;
export type LinkBioEditorTab = (typeof LINK_BIO_EDITOR_TABS)[number];

export function parseLinkBioEditorTab(value: unknown): LinkBioEditorTab | undefined {
  return LINK_BIO_EDITOR_TABS.find((tab) => tab === value);
}

/**
 * Every card the editor can jump to: the preview's clickable blocks plus the cards with no block on
 * the public page (`theme` is the Brand color card, which hosts the theme errors; `seo` is Google &
 * sharing, which only changes the page's `<head>`).
 */
export type LinkBioEditorSection = LinkBioPreviewEditSection | 'template' | 'theme' | 'shape' | 'seo';

export const LINK_BIO_SECTION_TAB: Record<LinkBioEditorSection, LinkBioEditorTab> = {
  header: 'profile',
  locations: 'profile',
  featured: 'profile',
  hours: 'profile',
  reviews: 'profile',
  gallery: 'profile',
  seo: 'profile',
  campaign: 'links',
  link: 'links',
  call: 'links',
  popup: 'links',
  social: 'links',
  template: 'design',
  theme: 'design',
  shape: 'design',
};

export interface LinkBioJumpTarget {
  section: LinkBioEditorSection;
  /** Button id (custom or built-in) or campaign id when a single card is the target. */
  itemId?: string;
}

/**
 * Call Us is a card in the button list now. A preview that still sends the bare `call` section (no
 * item id) opens that card like any other button.
 */
export function resolveLinkBioJumpTarget(target: LinkBioJumpTarget): LinkBioJumpTarget {
  return target.section === 'call' && !target.itemId
    ? { section: 'link', itemId: LINK_BIO_BUILT_IN_BUTTON_IDS.call }
    : target;
}

/**
 * The preview's chip and the editor's "Other locations" card read separately loaded contexts: if the
 * agency links the salon while the editor is open, the preview can show a chip the editor has no card
 * for. The click then lands on the Salon info card, where the chip sits on the page.
 */
export function linkBioJumpTargetWithFallback(target: LinkBioJumpTarget, hasOtherLocationsCard: boolean): LinkBioJumpTarget {
  return target.section === 'locations' && !hasOtherLocationsCard ? { section: 'header' } : target;
}

const PROFILE_FIELD_SECTION: Record<string, LinkBioEditorSection> = {
  featuredMediaUrl: 'featured',
};

/**
 * Card holding the first current validation error (in editor order), so "Publish" on an invalid draft
 * can take the owner straight to it. Paths come from `listLinkBioErrorPaths`.
 */
export function firstLinkBioErrorTarget(errors: LinkBioFormErrors): LinkBioJumpTarget | null {
  const [path] = listLinkBioErrorPaths(errors);
  if (!path) return null;
  const [root, itemId] = path.split('.');
  if (root === 'buttons' && itemId) return { section: 'link', itemId };
  if (root === 'socialLinks') return { section: 'social' };
  if (root === 'theme') return { section: 'theme' };
  if (root === 'reviews') return { section: 'reviews' };
  if (root === 'gallery') return { section: 'gallery' };
  if (root === 'popup') return { section: 'popup' };
  return { section: PROFILE_FIELD_SECTION[root] ?? 'header' };
}
